/**
 * The route a Xperience deep-link ("Start Test") asked us to land on, parked in
 * sessionStorage for the duration of the handoff.
 *
 * The deep-link carries its target in `/auth/<token>?redirect=…`, but that URL is
 * only alive for the few hundred milliseconds the token exchange takes. If anything
 * knocks the app off that URL in the meantime — a stale session being cleared, a
 * reload, a trip through /login — the target is gone and the student silently lands
 * on their home route instead of the exam they just clicked into. Parking it here
 * means the handoff can still finish from wherever the app ends up.
 *
 * sessionStorage (not localStorage): it belongs to this tab and this handoff only.
 */

const KEY = "exam-portal:pending-redirect";
// Long enough to survive a reload or a re-login, short enough that it can never
// hijack ordinary navigation later in the session.
const MAX_AGE_MS = 5 * 60 * 1000;

// Same-origin app paths only — never "//host" (protocol-relative) or a full URL.
const isSafePath = (path: unknown): path is string =>
  typeof path === "string" && /^\/(?!\/)/.test(path);

export function rememberPendingRedirect(path: string): void {
  if (!isSafePath(path)) return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() }));
  } catch {
    // Storage disabled/full — the in-URL redirect still covers the happy path.
  }
}

export function readPendingRedirect(): string {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return "";
    const { path, at } = JSON.parse(raw);
    if (!isSafePath(path) || typeof at !== "number" || Date.now() - at > MAX_AGE_MS) {
      sessionStorage.removeItem(KEY);
      return "";
    }
    return path;
  } catch {
    return "";
  }
}

export function clearPendingRedirect(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
